import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Megaphone, Send, Check, X, Image as ImageIcon } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

export default function AdvertiseRequestModal({ onClose }) {
  const [form, setForm] = useState({
    contact_name: "",
    company_name: "",
    company_vat: "",
    service_type: "",
    description: "",
    email: "",
    image_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const contactRef = useRef(null);
  const scrollRef = useRef(null);   // outer modal scroll container
  const successRef = useRef(null);  // success-message anchor (after submit)

  // Autofocus the first editable field when the modal mounts so the user
  // lands on the form immediately instead of having to scroll.
  useEffect(() => {
    const t = setTimeout(() => {
      // Scroll the modal container so the contact-name field is at the top
      // of the visible area. We do this on the outer scrollable div directly
      // (instead of scrollIntoView, which behaves inconsistently inside
      // nested scroll containers).
      if (scrollRef.current && contactRef.current) {
        const containerTop = scrollRef.current.getBoundingClientRect().top;
        const inputTop = contactRef.current.getBoundingClientRect().top;
        const target = scrollRef.current.scrollTop + (inputTop - containerTop) - 24;
        scrollRef.current.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
      }
      contactRef.current?.focus({ preventScroll: true });
    }, 80);
    return () => clearTimeout(t);
  }, []);

  // When the request is submitted successfully, scroll the success message
  // into view so the user sees the confirmation without manual scrolling.
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      successRef.current?.focus({ preventScroll: true });
    }, 50);
    return () => clearTimeout(t);
  }, [sent]);

  // Close on Escape for accessibility.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_500_000) { setErr("Image must be under 1.5 MB."); return; }
    const r = new FileReader();
    r.onload = () => setForm(s => ({ ...s, image_url: r.result }));
    r.readAsDataURL(f);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/advertise/request", form);
      trackEvent("advertise_request_sent");
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      ref={scrollRef}
      className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(8, 14, 11, 0.78)", backdropFilter: "blur(6px)" }}
      data-testid="advertise-request-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="card-hh w-full max-w-2xl my-6" style={{ background: "var(--hh-bg)" }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="label-eyebrow flex items-center gap-2"><Megaphone size={12}/> Advertise on Nomad Life</span>
            <h2 className="font-display text-3xl mt-1">Send your ad request</h2>
          </div>
          <button onClick={onClose} className="text-[var(--hh-sage)] hover:text-[var(--hh-cream)]" data-testid="advertise-close" aria-label="Close">
            <X size={20}/>
          </button>
        </div>

        {sent ? (
          <div className="mt-6 flex items-start gap-3" data-testid="advertise-success" ref={successRef} tabIndex={-1}>
            <Check size={18} color="var(--hh-accent)" className="mt-0.5"/>
            <div>
              <p className="font-display text-2xl">Request received.</p>
              <p className="text-sm text-[var(--hh-sage)] mt-1">
                Thank you. We'll review your request and reply to <span className="text-[var(--hh-cream)]">{form.email}</span> shortly. Once approved, you will receive an e-mail with a Stripe payment link. When your payment has been accepted, your ad will be visible on the platform.
              </p>
              <button onClick={onClose} className="btn-ghost mt-5" data-testid="advertise-success-close">Close</button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-5 card-hh" style={{ borderColor: "var(--hh-accent)" }} data-testid="advertise-pricing-block">
              <p className="font-display text-2xl">Ad for 1 year for €120</p>
              <p className="text-sm mt-1 text-[var(--hh-cream)]" style={{ fontStyle: "italic" }}>
                — Discounted price for the first year.
              </p>
              <p className="text-sm text-[var(--hh-sage)] mt-3">
                Once your advertisement request has been approved, you will receive an e-mail with a Stripe payment link.
                When your payment has been accepted, your ad will be visible on the platform.
              </p>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-5" data-testid="advertise-form">
              <div>
                <label className="label-eyebrow block mb-2">Subject</label>
                <input
                  className="input-hh"
                  value="Advertising on Nomad Life"
                  readOnly
                  data-testid="advertise-subject"
                />
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Contact name (first name and surname)</label>
                <input
                  ref={contactRef}
                  className="input-hh"
                  required
                  minLength={2}
                  value={form.contact_name}
                  onChange={e => setForm(s => ({ ...s, contact_name: e.target.value }))}
                  placeholder="e.g. Lukas Davis"
                  data-testid="advertise-contact-name"
                />
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Your email</label>
                <input
                  className="input-hh"
                  type="email"
                  required
                  value={form.email}
                  onChange={e => setForm(s => ({ ...s, email: e.target.value }))}
                  placeholder="you@company.com"
                  data-testid="advertise-email"
                />
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Company name</label>
                <input
                  className="input-hh"
                  required
                  minLength={2}
                  value={form.company_name}
                  onChange={e => setForm(s => ({ ...s, company_name: e.target.value }))}
                  data-testid="advertise-company-name"
                />
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Company VAT no.</label>
                <input
                  className="input-hh"
                  value={form.company_vat}
                  onChange={e => setForm(s => ({ ...s, company_vat: e.target.value }))}
                  placeholder="e.g. PT123456789"
                  data-testid="advertise-company-vat"
                />
                <p className="text-xs text-[var(--hh-sage)] mt-2" style={{ fontStyle: "italic" }}>
                  Automatic application of reverse VAT by Stripe for EU companies.
                </p>
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Type of service provided</label>
                <input
                  className="input-hh"
                  required
                  minLength={2}
                  value={form.service_type}
                  onChange={e => setForm(s => ({ ...s, service_type: e.target.value }))}
                  placeholder="e.g. Airport transfer, boat shuttle, camping site…"
                  data-testid="advertise-service-type"
                />
                <p className="text-xs text-[var(--hh-sage)] mt-2" style={{ fontStyle: "italic" }}>
                  Services are focused on nomad travelling / backpackers, such as van rental / 4-wheel-drive rental, boat shuttle, camping site / parking, jungle guide etc.
                </p>
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Description of service for the ad</label>
                <textarea
                  className="input-hh min-h-[180px]"
                  required
                  minLength={10}
                  value={form.description}
                  onChange={e => setForm(s => ({ ...s, description: e.target.value }))}
                  placeholder="Tell us about your service — what's included, locations, pricing, contact details…"
                  data-testid="advertise-description"
                />
              </div>

              {/* Picture upload — below the description, per spec */}
              <div data-testid="advertise-image-wrap">
                <label className="label-eyebrow block mb-2">Picture for the ad</label>
                {form.image_url ? (
                  <div className="relative inline-block" data-testid="advertise-image-preview">
                    <img src={form.image_url} alt="preview" className="max-h-64 border border-[var(--hh-border)]" />
                    <button
                      type="button"
                      onClick={() => setForm(s => ({ ...s, image_url: "" }))}
                      className="absolute top-1 right-1 bg-[var(--hh-bg)] border border-[var(--hh-border)] p-1 text-[var(--hh-cream)]"
                      data-testid="advertise-image-remove"
                      aria-label="Remove picture"
                    >
                      <X size={12}/>
                    </button>
                  </div>
                ) : (
                  <label className="border border-dashed border-[var(--hh-border)] h-32 max-w-md flex flex-col items-center justify-center text-xs text-[var(--hh-sage)] cursor-pointer hover:text-[var(--hh-accent)] hover:border-[var(--hh-accent)]">
                    <ImageIcon size={18}/>
                    <span className="mt-1">Add picture</span>
                    <input type="file" accept="image/jpeg,image/jpg,image/png" onChange={onFile} className="hidden" data-testid="advertise-image-input" />
                  </label>
                )}
                <p className="text-xs text-[var(--hh-sage)] mt-2">
                  Recommended size <strong>1200×900 px</strong> (4:3 ratio), <strong>JPG</strong> format, max <strong>1.5 MB</strong>.
                </p>
              </div>

              {err && <p className="text-sm text-[var(--hh-accent)]" data-testid="advertise-err">{err}</p>}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={onClose} className="btn-ghost text-sm" data-testid="advertise-cancel">Cancel</button>
                <button type="submit" disabled={busy} className="btn-primary text-sm flex items-center gap-2" data-testid="advertise-submit">
                  <Send size={12}/> {busy ? "Sending…" : "Send ad request"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
